import React from 'react';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * Notion 스타일 마크다운 뷰어 (개선판)
 * - GFM 지원 (테이블, 체크리스트)
 * - 코드 신택스 하이라이팅 적용
 * - CSS 디테일 강화
 */
const NotionViewer = ({ content, className }) => {
  if (!content) return null;

  return (
    <NotionContainer className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]} // 2. HTML 파싱 플러그인 추가
        components={{
          code({ node, inline, className, children, ...props }) {
            const isInline = node.position.end.line === node.position.start.line;
            const match = /language-(\w+)/.exec(className || '');
            if (!isInline) {
              return (
                <SyntaxHighlighter
                  style={oneLight}
                  language={match ? match[1] : 'text'} // 언어 미지정시 text
                  PreTag="div"
                  customStyle={{
                    margin: '1.5em 0',
                    borderRadius: '3px',
                    fontSize: '0.85em',
                    padding: '20px',
                    lineHeight: '1.5',
                    border: '1px solid rgba(55, 53, 47, 0.09)'
                  }}
                  {...props}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              );
            }
            return (
              <code className={`${className} notion-inline-code`} {...props}>
                {children}
              </code>
            );
          },
          a({ node, href, children, ...props }) {
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                {children}
              </a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </NotionContainer>
  );
};

export default NotionViewer;

const NotionContainer = styled.div`
  width: 100%;
  padding: 20px;
  margin: 0 auto;
  color: #37352f;
  font-size: 16px;
  line-height: 1.6;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol";
  background-color: #ffffff;

  // 세로 스크롤
  overflow-y: auto;
  height: 100%;
  scrollbar-width: thin;
  scrollbar-color: #f1f1ef #f1f1ef;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1ef;
  }

  .notion-inline-code {
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace;
    font-size: 85%;
    color: #eb5757; /* 노션 특유의 빨간색 */
    background: rgba(135, 131, 120, 0.15); /* 회색 배경 */
    padding: 0.2em 0.4em;
    border-radius: 3px;
  }

  /* 헤딩 */
  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
    color: #37352f;
    letter-spacing: -0.02em;
  }
  h1 { margin: 1em 0 0.5em; font-size: 2.2em; }
  h2 { margin: 1.7em 0 0.4em; font-size: 1.6em; border-bottom: 1px solid rgba(55, 53, 47, 0.09); padding-bottom: 4px;
    &:first-child { margin-top: 0; }
  }
  h3 { margin: 0.6em 0 0.3em; font-size: 1.35em; }
  h4 { margin: 0.5em 0 0.25em; font-size: 1.2em; }

  /* 리스트 */
  ul, ol { padding-left: 1.5em; margin: 0.3em 0; }
  ul li { list-style-type: disc; }
  ol li { list-style-type: decimal; }
  li { margin: 0.1em 0; }
  p { margin: 0.3em 0; min-height: 1em; }

  /* 인용구 (Callout) */
  blockquote {
    border-left: 3px solid #37352f;
    margin: 1em 0;
    padding: 0.5em 1em;
    background: transparent;
    font-style: italic;
  }

  /* Aside (Callout 박스) */
  aside {
    background-color: #f1f1ef;
    padding: 16px;
    border-radius: 3px;
    margin: 1em 0;
    display: flex;
    align-items: flex-start;
  }
  aside p { margin: 0; }

  /* 테이블 */
  table {
    display: block;
    width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
    margin: 1em 0;
    font-size: 15px;
  }
  th, td {
    border: 1px solid rgba(55, 53, 47, 0.09);
    padding: 7px 10px;
    min-width: 120px;
  }
  th { background-color: #f7f6f3; font-weight: 600; text-align: left; }

  /* 링크 */
  a {
    color: inherit;
    text-decoration: none;
    border-bottom: 1px solid rgba(55, 53, 47, 0.4);
    opacity: 0.7;
    transition: opacity 0.2s;
  }
  a:hover { opacity: 1; }

  /* 체크박스 */
  input[type="checkbox"] {
    appearance: none;
    width: 1.1em;
    height: 1.1em;
    border: 1px solid currentColor;
    border-radius: 3px;
    margin-right: 0.5em;
    position: relative;
    top: 0.2em;
  }
  input[type="checkbox"]:checked {
    background-color: #2eaadc;
    border-color: #2eaadc;
  }
  input[type="checkbox"]:checked::after {
    content: '✔';
    display: block;
    color: white;
    font-size: 0.8em;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  /* 이미지 */
  img { max-width: 100%; border-radius: 3px; margin: 1.5em 0; }

  /* 구분선 */
  hr { margin: 2em 0; border: 0; border-top: 1px solid rgba(55, 53, 47, 0.09); }
`;